import * as React from "react";
import * as AppPath from "../model/AppPath";
import download from "downloadjs";
import JSONbig from "json-bigint";

import { Utilities } from "../../shared/utilities";

import {
    ProcessTemplateAppReducerAction, SET_LOADING, SET_PROCESS_TEMPLATES, SET_PROCESS_TEMPLATES_COUNT,
    SET_AVAILABLE_TAGS, SET_EDITED_TEMPLATE, SET_TEMPLATE_FOR_CREATE, SET_VIEW_CHANGE_LOG_FOR_TEMPLATE_ID,
    SET_USED_IN_MODAL_OPEN, SET_DELETED_TEMPLATE_IDS, SET_TAGGED_TEMPLATE_IDS, SET_SELECTED_TEMPLATE_IDS,
    SET_MISSING_TASKS,
    // SET_SWITCH_VERSION_FOR_TEMPLATE_ID, SET_IMPORT_MODAL_OPEN, SET_CONFIRM_PROCESS_UPDATES_FOR_TEMPLATE_IMPORT
} from "../reducers/AppReducer";
import { CreateProcessInfo, GetProcessTemplatesResponse, ProcessTemplate /*ProcessTemplateImport*/ } from "processTemplates/model/ProcessTemplate";
import { UsedInModalState } from "processTemplates/model/AppState";
import { MissingTask } from "processTemplates/model/MissingTask";
import { changedPinnedElementsKey } from "processList/actions/ProcessListActions";

export const PROCESS_TEMPLATE_BASE_URL = 'api/processTemplate';

export const handleApiError = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, e: any) => {
    setLoading(dispatch, false);
    Utilities.toastError(e);
    console.error(e);
};

export const setLoading = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, loading: boolean) => {
    dispatch({ type: SET_LOADING, loading });
};

export const setProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, response?: GetProcessTemplatesResponse) => {
    dispatch({ type: SET_PROCESS_TEMPLATES, processTemplates: response?.templates });
    dispatch({ type: SET_PROCESS_TEMPLATES_COUNT, processTemplatesCount: response?.templateCount });
};

export const getProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>) => {
    (async () => {
        try {
            const searchParams = AppPath.getSearchParameters() as unknown as { [key: string]: string };
            const response: GetProcessTemplatesResponse = await Utilities.getFetch(`${PROCESS_TEMPLATE_BASE_URL}`, searchParams);
            setProcessTemplates(dispatch, response);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const getAvailableTags = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>) => {
    (async () => {
        try {
            const availableTags: string[] = await Utilities.getFetch(`api/tag`);
            dispatch({ type: SET_AVAILABLE_TAGS, availableTags });
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const copyProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateIds: number[]) => {
    (async () => {
        try {
            await Utilities.postJsonAndParseResponse(`${PROCESS_TEMPLATE_BASE_URL}/copy`, { templateIds });
            getProcessTemplates(dispatch);
            Utilities.toastSuccess(FRENDS.Localizations.Flash_CopySuccess);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const deleteProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateIds: number[]) => {
    (async () => {
        try {
            await Utilities.deleteFetchAndParseResponse(`${PROCESS_TEMPLATE_BASE_URL}`, { templateIds });
            dispatch({ type: SET_DELETED_TEMPLATE_IDS, deletedTemplateIds: undefined });
            getProcessTemplates(dispatch);
            Utilities.toastSuccess(FRENDS.Localizations.Flash_DeleteSuccess);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const exportProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateIds: number[]) => {
    (async () => {
        try {
            const response = await Utilities.getFetchWithHeaders(`${PROCESS_TEMPLATE_BASE_URL}/export`, { templateIds: templateIds.map((id) => String(id)) });
            const fileName = Utilities.parseFileNameFromContentDisposition(response.headers.get("Content-Disposition"));
            download(
                // ACC-8685 Add UTF-8 header to the data. This fixes some encoding issues.
                new Blob(["\ufeff", JSONbig.stringify(response.content as any, null, "\t")]),
                fileName ?? "ProcessTemplateExport.json",
                "application/json; charset=utf-8");
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const saveAllProcessTemplatesAsCsv = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>) => {
    (async () => {
        try {
            const response = await Utilities.getFetchWithHeaders(`${PROCESS_TEMPLATE_BASE_URL}/downloadAll`);
            const fileName = Utilities.parseFileNameFromContentDisposition(response.headers.get("Content-Disposition"));
            download(response.content as any, fileName ?? "allProcessTemplateDownload.csv", "text/csv")
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const saveProcessTemplatesAsCsv = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateIds: number[]) => {
    (async () => {
        try {
            const response = await Utilities.getFetchWithHeaders(`${PROCESS_TEMPLATE_BASE_URL}/download`, { templateIds: templateIds.map((id) => String(id)) });
            const fileName = Utilities.parseFileNameFromContentDisposition(response.headers.get("Content-Disposition"));
            download(response.content as any, fileName ?? "processTemplateDownload.csv", "text/csv")
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const tagProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateIds: number[], tags: string[], overwrite: boolean) => {
    (async () => {
        try {
            await Utilities.postJsonAndParseResponse(`${PROCESS_TEMPLATE_BASE_URL}/tag`, { templateIds, tags, overwrite });
            getProcessTemplates(dispatch);
            setSelectedTemplateIds(dispatch, []);
            setTaggedTemplateIds(dispatch, undefined);
            getAvailableTags(dispatch)
            Utilities.toastSuccess(FRENDS.Localizations.Flash_ElementTaggedSuccessfully);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const pinProcessTemplates = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateGuids: string[]) => {
    (async () => {
        try {
            await Utilities.putJsonAndParseResponse("api/user/config/pinProcessTemplates", { [changedPinnedElementsKey]: templateGuids });
            Utilities.toastSuccess("Updated Template pin.");
            getProcessTemplates(dispatch);
            setSelectedTemplateIds(dispatch, []);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

export const createProcessFromTemplate = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateId: number, processInfo: CreateProcessInfo) => {
    (async () => {
        try {
            setLoading(dispatch, true);
            await Utilities.postJsonAndParseResponse<string>(`${PROCESS_TEMPLATE_BASE_URL}/${templateId}/createProcess`, processInfo);
            setCreateModalOpen(dispatch, undefined);
            getProcessTemplates(dispatch);
            Utilities.toastSuccess(`Process ${processInfo.name} created`);
            setLoading(dispatch, false);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    })();
};

// export const setImportModalOpen = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, open: boolean) => {
//     dispatch({ type: SET_IMPORT_MODAL_OPEN, open });
// };

// export const setConfirmProcessUpdatesForTemplateImport = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, confirmProcessUpdatesForTemplateImport?: ProcessTemplateImport) => {
//     dispatch({ type: SET_CONFIRM_PROCESS_UPDATES_FOR_TEMPLATE_IMPORT, confirmProcessUpdatesForTemplateImport });
// };

export const setEditModeModalOpen = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, editedTemplate?: ProcessTemplate) => {
    dispatch({ type: SET_EDITED_TEMPLATE, editedTemplate });
};

export const setCreateModalOpen = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, templateForCreate?: ProcessTemplate) => {
    dispatch({ type: SET_TEMPLATE_FOR_CREATE, templateForCreate });
};

export const setChangeLogModalOpen = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, viewChangeLogForTemplateId?: number) => {
    dispatch({ type: SET_VIEW_CHANGE_LOG_FOR_TEMPLATE_ID, viewChangeLogForTemplateId });
};

export const setUsedInModalOpen = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, usedInModalState?: UsedInModalState) => {
    dispatch({ type: SET_USED_IN_MODAL_OPEN, usedInModalState });
};

// export const setSwitchVersionModalOpen = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, switchVersionForTemplateId?: number) => {
//     dispatch({ type: SET_SWITCH_VERSION_FOR_TEMPLATE_ID, switchVersionForTemplateId });
// };

export const setDeleteTemplateIds = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, deletedTemplateIds?: number[]) => {
    dispatch({ type: SET_DELETED_TEMPLATE_IDS, deletedTemplateIds });
};

export const setTaggedTemplateIds = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, taggedTemplateIds?: number[]) => {
    dispatch({ type: SET_TAGGED_TEMPLATE_IDS, taggedTemplateIds });
};

export const setSelectedTemplateIds = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, selectedTemplateIds: number[]) => {
    dispatch({ type: SET_SELECTED_TEMPLATE_IDS, selectedTemplateIds });
}

export const setMissingTasks = (dispatch: React.Dispatch<ProcessTemplateAppReducerAction>, missingTasks: MissingTask[]) => {
    dispatch({ type: SET_MISSING_TASKS, missingTasks });
}
